import { EMPTY_OBJECT, isNullish } from '../';
import type { Entries, Nullish } from '../../types/utility';

const EN_US = 'en-US';

// predefine common formatters to improve performance on multiple calls
const DATE_TIME_FORMAT_OPTIONS = {
  Default: {},
  'MONTH DD, YYYY, HH:MI TZ': {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  },
  'MONTH DD, YYYY, HH:MI': {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  },
  'MON DD, YYYY, HH:MI TZ': {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  },
  'MON DD, YYYY, HH:MI': {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  },
  'MON DD YYYY': {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  },
  'MONTH DD HH:MI': {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  },
  'MONTH DD': { month: 'long', day: 'numeric' },
  'MON DD': { month: 'short', day: 'numeric' },
  'MM/DD/YYYY, HH:MI TZ': {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  },
  'MM/DD/YYYY, HH:MI': {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  },
  'MM/DD/YYYY': { month: 'numeric', day: 'numeric', year: 'numeric' },
  'MM/DD/YY': { month: 'numeric', day: 'numeric', year: '2-digit' },
  'MM/YYYY': { month: 'numeric', year: 'numeric' },
  YYYY: { year: 'numeric' },
  'HH:MI TZ': { hour: 'numeric', minute: 'numeric', timeZoneName: 'short' },
  'HH:MI': { hour: 'numeric', minute: 'numeric' },
};

const DATE_TIME_FORMATTERS = (
  Object.entries(DATE_TIME_FORMAT_OPTIONS) as Entries<typeof DATE_TIME_FORMAT_OPTIONS>
).reduce(
  (formats, [format, options]) => {
    return {
      ...formats,
      [format]: new Intl.DateTimeFormat(EN_US, options as Intl.DateTimeFormatOptions),
    };
  },
  {} as Record<keyof typeof DATE_TIME_FORMAT_OPTIONS, Intl.DateTimeFormat>,
);

type DateTime = {
  year?: number;
  month?: number;
  day?: number;
  hours?: number;
  minutes?: number;
};

const DATE_KEYS = new Set(['year', 'month', 'day', 'hours', 'minutes']);

/**
 *
 * @param {(Date|string|number|DateTime)} input A date to format that can be: JS Date; ISO or GMT string; timestamp in milliseconds; or object in the shape of { year?: number, month?: number, day?: number, hours?: number, minutes?: number }
 * @param {object|string} options An Intl.DateTimeFormatOptions object or a format string
 *
 * @example ISO string example
 * '2024-01-01T00:00:00.000Z'
 *
 * @example GMT string example
 * '01 Jan 2024 00:00:00 GMT'
 *
 * @example Format string examples (given '2024-01-01T00:00:00.000Z')
 * 'MONTH DD, YYYY, HH:MI TZ' => 'January 1, 2024 at 12:00 AM UTC'
 * 'MONTH DD, YYYY, HH:MI' => 'January 1, 2024 at 12:00 AM'
 * 'MON DD, YYYY, HH:MI TZ' => 'Jan 1, 2024, 12:00 AM UTC'
 * 'MON DD, YYYY, HH:MI' => 'Jan 1, 2024, 12:00 AM'
 * 'MON DD YYYY' => 'Jan 1, 2024'
 * 'MONTH DD' => 'January 1'
 * 'MONTH DD HH:MI' => 'January 1 12:35 PM'
 * 'MON DD' => 'Jan 1'
 * 'MM/DD/YYYY, HH:MI TZ' => '1/1/2024, 12:00 AM UTC'
 * 'MM/DD/YYYY, HH:MI' => '1/1/2024, 12:00 AM'
 * 'MM/DD/YYYY' => '1/1/2024'
 * 'MM/DD/YY' => '1/1/24'
 * 'MM/YYYY' => '1/2024'
 * 'YYYY' => '2024'
 * 'HH:MI TZ' => '12:00 AM UTC'
 * 'HH:MI' => '12:00 AM'
 * 'YYYY-MM-DD' => '2024-01-01'
 * @returns The input formatted as a Date string, or the input's string value if it's not a valid date
 */
export function formatDate(
  input: Date | string | number | DateTime | Nullish,
  options:
    | Intl.DateTimeFormatOptions
    | keyof typeof DATE_TIME_FORMATTERS
    | 'YYYY-MM-DD'
    | {
        type: 'iso' | 'iso-local';
        format:
          | 'YYYY'
          | 'YYYY-MM'
          | 'YYYY-MM-DD'
          | 'YYYY-MM-DDThh'
          | 'YYYY-MM-DDThh:mm'
          | 'YYYY-MM-DDThh:mm:ss';
      } = 'Default',
  timeZone?: string,
) {
  let date: Date;

  if (isNullish(input) || input === '') {
    return '';
  }

  if (typeof input === 'string' || typeof input === 'number') {
    const parsedInput = +input; // for a string version of a timestamp (e.g. '1749231900000')
    date = new Date(parsedInput || input);
  } else if (input instanceof Date) {
    date = input;
  } else if (Object.keys(input).some((key) => DATE_KEYS.has(key))) {
    date = new Date(
      input.year ?? 0,
      input.month ? input.month - 1 : 0,
      input.day ?? 1,
      input.hours ?? 0,
      input.minutes ?? 0,
    );
  } else {
    date = new Date();
  }

  const isInvalidDate = isNaN(date.getTime()); // invalid dates will have NaN fields
  if (isInvalidDate) {
    return String(input);
  }

  if (timeZone && typeof options === 'string') {
    const formatOptions =
      DATE_TIME_FORMAT_OPTIONS[options as keyof typeof DATE_TIME_FORMAT_OPTIONS] || EMPTY_OBJECT;
    return date.toLocaleString(EN_US, { ...formatOptions, timeZone });
  }

  const formatter =
    typeof options === 'string'
      ? DATE_TIME_FORMATTERS[options as keyof typeof DATE_TIME_FORMATTERS]
      : null;

  if (formatter) {
    return formatter.format(date);
  } else if (options === 'YYYY-MM-DD') {
    return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
      .map((value) => (value < 10 ? `0${value}` : value))
      .join('-');
  }

  if (!!options && typeof options === 'object') {
    if ('type' in options) {
      const { type, format } = options;
      let iso;

      if (type === 'iso-local') {
        if (timeZone) {
          const parts = new Intl.DateTimeFormat(EN_US, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone,
          }).formatToParts(date);
          const find = ((parts: Intl.DateTimeFormatPart[]) => {
            return (type: string) => {
              return parts.find((part) => type === part.type)?.value || '00';
            };
          })(parts);

          iso = `${find('year')}-${find('month')}-${find('day')}T${find('hour')}:${find(
            'minute',
          )}:${find('second')}`;
        } else {
          const local = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000); // getTimezoneOffset (in minutes * 60s * 1000ms)
          iso = local.toISOString().slice(0, -1); // remove trailing Z
        }
      } else {
        iso = date.toISOString();
      }

      return iso.substring(0, format.length) + (type === 'iso' && format.includes('T') ? 'Z' : '');
    }

    return date.toLocaleString(EN_US, options);
  }

  return DATE_TIME_FORMATTERS.Default.format(date);
}

formatDate.formatters = DATE_TIME_FORMATTERS;
